import React, { cloneElement } from 'react';
import { AlertCircle, Clock, Battery, HelpCircle } from 'lucide-react';
export function ProblemSection() {
  return <section className="py-20 px-4 bg-white" data-id="element-187">
      <div className="max-w-4xl mx-auto" data-id="element-188">
        <div className="text-center mb-16" data-id="element-189">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-190">
            Enseñar Teoría de las Organizaciones no debería ser una carga mental
            constante.
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed" data-id="element-191">
            No debería quitarte horas de descanso. No debería obligarte a
            improvisar cada semana.
            <br data-id="element-192" />
            <span className="font-semibold text-blue-800" data-id="element-193">
              Y sin embargo, para muchos docentes, eso es exactamente lo que
              pasa.
            </span>
          </p>
        </div>

        <div className="bg-gray-50 rounded-2xl p-8 md:p-12 border border-gray-100 shadow-sm mb-16" data-id="element-194">
          <p className="text-lg text-gray-700 mb-6" data-id="element-195">
            Si sos docente de nivel secundario en la modalidad Comercial,
            Contable, Economía o Administración, probablemente vivís esta
            realidad:
          </p>
          <div className="flex items-start gap-4 mb-2" data-id="element-196">
            <div className="bg-blue-100 p-2 rounded-full text-blue-700 mt-1" data-id="element-197">
              <CheckCircleIcon data-id="element-198" />
            </div>
            <p className="text-lg font-medium text-gray-800" data-id="element-199">
              Tenés el programa oficial.
            </p>
          </div>
          <div className="flex items-start gap-4 mb-2" data-id="element-200">
            <div className="bg-blue-100 p-2 rounded-full text-blue-700 mt-1" data-id="element-201">
              <CheckCircleIcon data-id="element-202" />
            </div>
            <p className="text-lg font-medium text-gray-800" data-id="element-203">
              Sabés el contenido.
            </p>
          </div>
          <div className="flex items-start gap-4 mb-6" data-id="element-204">
            <div className="bg-blue-100 p-2 rounded-full text-blue-700 mt-1" data-id="element-205">
              <CheckCircleIcon data-id="element-206" />
            </div>
            <p className="text-lg font-medium text-gray-800" data-id="element-207">
              Querés dar buenas clases.
            </p>
          </div>

          <div className="bg-white border-l-4 border-orange-500 p-6 shadow-sm" data-id="element-208">
            <p className="text-2xl font-bold text-gray-900 mb-2" data-id="element-209">
              Pero no tenés un sistema.
            </p>
            <p className="text-gray-600" data-id="element-210">
              Y cuando no hay sistema, todo cuesta el doble.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-211">
          <div data-id="element-212">
            <h3 className="text-2xl font-bold text-blue-900 mb-6" data-id="element-213">
              El problema no es la materia. <br data-id="element-214" />
              <span className="text-orange-600" data-id="element-215">Tampoco sos vos.</span>
            </h3>
            <p className="text-gray-700 mb-4 leading-relaxed" data-id="element-216">
              El verdadero problema es que{' '}
              <strong data-id="element-217">
                nadie diseñó materiales de Teoría de las Organizaciones pensados
                para la realidad docente actual.
              </strong>
            </p>
            <p className="text-gray-700 mb-4 leading-relaxed" data-id="element-218">
              Hay libros. Hay apuntes. Hay PDFs sueltos.
            </p>
            <p className="text-gray-700 font-medium" data-id="element-219">
              Pero no hay un recorrido anual claro, ordenado y listo para el
              aula.
            </p>
          </div>

          <div className="bg-red-50 rounded-xl p-8 border border-red-100" data-id="element-220">
            <h4 className="text-red-800 font-bold mb-4 flex items-center gap-2" data-id="element-221">
              <AlertCircle className="h-5 w-5" data-id="element-222" />
              Esto genera:
            </h4>
            <ul className="space-y-4" data-id="element-223">
              <ProblemItem icon={<Clock data-id="element-225" />} text="Clases improvisadas" data-id="element-224" />
              <ProblemItem icon={<HelpCircle data-id="element-227" />} text="Alumnos desconectados" data-id="element-226" />
              <ProblemItem icon={<AlertCircle data-id="element-229" />} text="Evaluaciones poco claras" data-id="element-228" />
              <ProblemItem icon={<Clock data-id="element-231" />} text="Sensación constante de estar 'corriendo atrás'" data-id="element-230" />
              <ProblemItem icon={<Battery data-id="element-233" />} text="Agotamiento mental a mitad de año" data-id="element-232" />
            </ul>
          </div>
        </div>

        <div className="mt-12 text-center" data-id="element-234">
          <p className="text-xl font-medium text-gray-800 italic" data-id="element-235">
            "Cuando esto se repite, enseñar deja de ser vocación y se vuelve
            supervivencia."
          </p>
        </div>
      </div>
    </section>;
}
function ProblemItem({
  icon,
  text
}: {
  icon: React.ReactNode;
  text: string;
}) {
  return <li className="flex items-center gap-3 text-red-900/80" data-id="element-236">
      <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 text-red-600" data-id="element-237">
        {cloneElement(icon as React.ReactElement, {
        size: 16
      })}
      </div>
      <span data-id="element-238">{text}</span>
    </li>;
}
function CheckCircleIcon() {
  return <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-239">
      <polyline points="20 6 9 17 4 12" data-id="element-240"></polyline>
    </svg>;
}